/**
 * Patient store (PROD-579 + PROD-568 persistence).
 *
 * Holds the active patient id; the fixture itself is resolved from
 * `src/data/synthetic-patients` so the store stays small (no inlined
 * clinical data in localStorage).
 *
 * Per ADR-PROTO-006, persistence keys follow `cps:${userId}:patient:active`.
 * Phase 1 with anonymous user is `cps:anonymous:patient:active`. PROD-568
 * wires the actual localStorage read (on module load) and write (on every
 * setter) so a refresh restores the last active patient.
 *
 * Per HC9 + COMPONENT-CONTRACTS §6 (PatientPicker), `setActivePatient`
 * emits a `patient_change` telemetry event with:
 *   - from_patient_id (or null on first selection)
 *   - to_patient_id
 *   - latency_ms (real performance.now() delta)
 *   - correlation_id
 *
 * `setActivePatientId` is exposed for the cross-store invariant (PROD-568
 * tenant switch). It writes the id silently (no telemetry event) because
 * the originating action already emitted `tenant_change`.
 */
import { create } from 'zustand';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';
import {
  getDefaultPatient,
  getPatientById,
  type PatientFixture,
} from '../data/synthetic-patients';
import { useUserStore } from './user.store';

export interface PatientState {
  activePatientId: string | null;
  setActivePatient: (id: string) => void;
  /**
   * Set the active patient id without emitting `patient_change`. Used by
   * the cross-store invariant on tenant change so the originating
   * `tenant_change` event remains the single source of truth.
   */
  setActivePatientId: (id: string) => void;
  /** Resolve the active patient fixture, or `null` if no active id is set. */
  getActivePatient: () => PatientFixture | null;
}

/** localStorage key per ADR-PROTO-006 userId-keying convention. */
export function patientStorageKey(userId: string): string {
  return `cps:${userId}:patient:active`;
}

/**
 * Read the persisted active patient id for the current user. Returns
 * `null` if no value is persisted, the persisted id is unknown, or
 * localStorage is unavailable (private browsing, server render).
 */
function readPersistedPatientId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const userId = useUserStore.getState().userId;
    const raw = localStorage.getItem(patientStorageKey(userId));
    if (!raw) return null;
    return getPatientById(raw) ? raw : null;
  } catch (err) {
    console.warn('[patient.store] localStorage read failed; using default', err);
    return null;
  }
}

/**
 * Best-effort persist the active patient id for the current user.
 * Failures (quota, private browsing) log a warning but do not throw.
 */
function persistPatientId(id: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const userId = useUserStore.getState().userId;
    const key = patientStorageKey(userId);
    if (id === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, id);
    }
  } catch (err) {
    console.warn('[patient.store] localStorage write failed', err);
  }
}

// Phase 1 default: persisted value if present, else first patient in
// canonical order (Khalid). PROD-568 cross-store invariant moves the
// active id to a tenant-appropriate patient on tenant change.
const DEFAULT_PATIENT_ID =
  readPersistedPatientId() ?? getDefaultPatient().id;

export const usePatientStore = create<PatientState>((set, get) => ({
  activePatientId: DEFAULT_PATIENT_ID,
  setActivePatient: (id) => {
    const target = getPatientById(id);
    if (!target) {
      // Match the tenant-store discipline: a typo cannot wedge the store.
      console.warn(
        `[patient.store] setActivePatient rejected unknown id "${id}"; keeping current value`,
      );
      return;
    }
    const from = get().activePatientId;
    if (from === id) return;

    const start = performance.now();
    set({ activePatientId: id });
    persistPatientId(id);
    const latency = Math.round(performance.now() - start);

    emitTelemetry('patient_change', {
      from_patient_id: from,
      to_patient_id: id,
      latency_ms: latency,
      correlation_id: newCorrelationId(),
    });
  },
  setActivePatientId: (id) => {
    const target = getPatientById(id);
    if (!target) {
      console.warn(
        `[patient.store] setActivePatientId rejected unknown id "${id}"; keeping current value`,
      );
      return;
    }
    if (get().activePatientId === id) return;
    set({ activePatientId: id });
    persistPatientId(id);
  },
  getActivePatient: () => {
    const id = get().activePatientId;
    if (!id) return null;
    return getPatientById(id) ?? null;
  },
}));
